import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Section } from '../../02-Molecules/Section/Section'
import { threeDSecureVerifyCard } from '../../Utility/BrainTree'
import { billingAddressGet, checkAddressIsValid, dataLayerPushError } from '../../Utility/WoodGreenPaymentsApi'
import { dataLayerPushDonationFailed } from '../../Utility/Utility'

import './SectionCardDetails.scss'

/**
 * SectionCardDetails component.
 */
export const SectionCardDetails = forwardRef(({ brainTreeInstancesGet, brainTreeIsInstantiated, collapsed, heading, headingLarge,
   loqateKey, onDoNext, onSetProcessing, paymentHasCompleted, sectionId, tracking, trackEvent, validationHash, values, visuallyHidden, donationFeeRate, ...props }, ref) => {
  const parent = useRef()
  const [paymentError, paymentErrorSet] = useState('')
  const [validState, validStateSet]     = useState(false)

  /**
   * Scroll the error message element into view.
   */
  const errorScrollIntoView = () => {
    const errorMessage = document.body.querySelector('.o-section-card-details__payment-error')
    if (errorMessage) {
      errorMessage.scrollIntoView()
    }
  }

  /**
   * Handle the external error.
   *
   * @param {string} message
   *   The message to set.
   */
  const handleExternalError = message => {
    paymentErrorSet(message)
    errorScrollIntoView()
  }

  /**
   * Set the state of the details field.
   *
   * @type {string}
   */
  const detailsState = brainTreeIsInstantiated ? 'active' : 'inactive'

  /**
   * Handle the card payment.
   */
  const handlerCardDoPayment = () => {
    return new Promise((resolve, reject) => {
      if (values.order_id || values.payment_nonce) {
        resolve({})
      }
      else if (brainTreeIsInstantiated && validState) {
        const brainTreeInstances = brainTreeInstancesGet()
        brainTreeInstances.hostedFields.tokenize({ billingAddress: billingAddressGet(values) })
          .then(payload => brainTreeInstances.threeDSecure.verifyCard(threeDSecureVerifyCard(payload, values, donationFeeRate)))
          .then(payload => {
            if (payload['threeDSecureInfo']['liabilityShifted']) {
              resolve({
                payment_nonce      : payload.nonce,
                payment_client_data: brainTreeInstances.dataCollector['deviceData'],
                payment_provider   : 'Braintree',
                payment_method     : 'Card',
              })
            }
            else {
              throw {
                message: 'Try a different card. Your bank can’t verify your information. You should also disable any pop-up blockers you have running.'
              }
            }
          })
          .catch(err => {
            dataLayerPushError(err)

            const errorEvent = {
              status: 'n/a',
              reason: 'n/a',
            }

            // These 2 props has to be present at the same time
            if (err.processorResponseStatus) {
              errorEvent.status = err.processorResponseStatus
              if (err.rejectReason) {
                errorEvent.reason = err.rejectReason
              }
            } else if (err.name && err.name == "BraintreeError") {
              errorEvent.status = err.details.originalError.details.httpStatus
              errorEvent.reason = err.details.originalError.details.originalError.error.message
            } else if(err.message) {
              errorEvent.reason = err.message
            }

            dataLayerPushDonationFailed(errorEvent.status, errorEvent.reason)

            paymentErrorSet(err.message)
            errorScrollIntoView()
            reject()
          })
      }
      else {
        reject()
      }
    })
  }

  /**
   * Handle the validity change on the Braintree hosted fields and billing address.
   *
   * @param {{}} state
   *   The current state of the hosted fields
   */
  const handlerOnValidityChange = state => {
    const cardIsValid = Object.keys(state.fields).reduce((formValid, currentKey) => {
      return state.fields[currentKey]['isValid'] ? formValid : false;
    }, true)

    const totalPrice = (donationFeeRate && values.cover_donation_fee === 'Y')
    ? (parseFloat(values.payment_amount) * donationFeeRate).toFixed(2).replace('.00', '')
    : values.payment_amount;

    const addressIsValid = values.use_contact_address ? checkAddressIsValid('contact_address', values) : checkAddressIsValid('billing_address', values)
    validStateSet(cardIsValid && addressIsValid && !isNaN(parseFloat(totalPrice)))
  }

  /**
   * Determine if the component is in a valid state.
   */
  const isValid = () => validState || values.order_id !== '' || values.payment_nonce !== ''

  /**
   * Render the client token error.
   *
   * @returns {JSX.Element}
   */
  const renderClientTokenError = () => {
    return (
      <div className={'o-section-card-details__client-error'}>
        An unexpected error occurred, <button onClick={() => location.reload()}>try again</button>
      </div>
    )
  }

  /**
   * Render the payment fields.
   *
   * @returns {JSX.Element}
   */
  const renderHostedFields = () => {
    return (
      <div className={'o-section-card-details__fields'} data-state={detailsState} data-error={paymentError !== ''}>
        {paymentError &&
        <div className={'b-form-row'} >
          <div className={'o-section-card-details__payment-error'} role="alert">
            {paymentError}
          </div>
        </div>
        }

        <div className={'b-form-row'}>
          <label htmlFor="card-number">Card Number</label>
          <div id="card-number" className={'o-section-card-details__field'}/>
        </div>

        <div className={'o-section-card-details__date-fields'}>
          <div className={'b-form-row'} >
            <label htmlFor="expiration-date-month">Expiration month</label>
            <div id="expiration-date-month" className={'o-section-card-details__field'} />
          </div>

          <div className={'b-form-row'} >
            <label htmlFor="expiration-date-year">Expiration year</label>
            <div id="expiration-date-year" className={'o-section-card-details__field'} />
          </div>
        </div>

        <div className={'b-form-row'} >
          <div className="card-details__cvv-tooltip-wrapper">
            <label htmlFor="cvv">CVV</label>
            <div className="card-details__cvv-tooltip">
              <button aria-labelledby="cvv-tooltip" className="card-details__cvv-tooltip-icon"><span>?</span></button>
              <div role="tooltip" id="cvv-tooltip" className="card-details__cvv-tooltip-text">Enter the last 3 digits printed on the signature strip on the back of your card. </div>
            </div>
          </div>
          <div id="cvv" className={'o-section-card-details__field'} />
        </div>
      </div>
    )
  }

  /**
   * Render the Payment complete state.
   *
   * @returns {JSX.Element}
   */
  const renderPaymentComplete = () => {
    return (
      <div>
        Your payment is complete.
        For security your card details have not been stored
      </div>
    )
  }

  useImperativeHandle(
    ref,
    () => ({
      doAction: () => handlerCardDoPayment(),
      isValid : () => isValid(),
      setError: message => handleExternalError(message),
    }),
  )

  useEffect(() => {
    if (brainTreeIsInstantiated) {
      const brainTreeInstances = brainTreeInstancesGet()
      brainTreeInstances.hostedFields.on('validityChange', event => handlerOnValidityChange(brainTreeInstances.hostedFields.getState()))
    }
  })

  useEffect(() => {
      if (brainTreeIsInstantiated) {
        handlerOnValidityChange(brainTreeInstancesGet().hostedFields.getState())
      }
    }, [
    values.payment_amount,
    values.use_contact_address,
    values.contact_address_line_1,
    values.contact_address_line_2,
    values.contact_address_town_city,
    values.contact_address_post_code,
    values.billing_address_line_1,
    values.billing_address_line_2,
    values.billing_address_town_city,
    values.billing_address_post_code,
    values.cover_donation_fee
  ])

  useEffect(() => {
    if (brainTreeIsInstantiated) {
      const currentState = brainTreeInstancesGet().hostedFields.getState()

      Object.keys(currentState.fields).forEach((currentKey) => {
        if (!currentState.fields[currentKey]['isValid']) {
          currentState.fields[currentKey]['container'].classList.add('braintree-hosted-fields-invalid')
        }
      })
    }
  }, [validationHash])


  return (
    <Section tracking={tracking} trackEvent={trackEvent} collapsed={collapsed} heading={heading} headingLarge={headingLarge}
    sectionId={sectionId} visuallyHidden={visuallyHidden}>
      <div ref={parent} className={'o-section-card-details'} data-error={paymentError}>
        {brainTreeIsInstantiated === false ?
          renderClientTokenError() : paymentHasCompleted ? renderPaymentComplete() : renderHostedFields()}
      </div>
    </Section>
  )
})


/**
 * SectionCardDetails PropTypes.
 */
SectionCardDetails.propTypes = {
  brainTreeIsInstantiated : PropTypes.bool,
  brainTreeInstancesGet   : PropTypes.func,
  loqateKey               : PropTypes.string,
  collapsed               : PropTypes.bool,
  paymentHasCompleted     : PropTypes.bool,
  paymentError            : PropTypes.bool,
  heading                 : PropTypes.string,
  headingLarge            : PropTypes.bool,
  sectionId               : PropTypes.string,
  validationHash          : PropTypes.string,
  onInputChange           : PropTypes.func,
  onSetProcessing         : PropTypes.func,
  visuallyHidden          : PropTypes.bool,
  staticText              : PropTypes.shape({
    card_details: PropTypes.shape({
      text_before           : PropTypes.string,
      text_after            : PropTypes.string,
    })
  }),
  tracking: PropTypes.shape({
    id  : PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func,
  values: PropTypes.shape({
    payment_amount           : PropTypes.string,
    payment_nonce            : PropTypes.string,
    first_name               : PropTypes.string,
    last_name                : PropTypes.string,
    email_address            : PropTypes.string,
    telephone_number         : PropTypes.string,
    use_contact_address      : PropTypes.bool,
    contact_address_line_1   : PropTypes.string,
    contact_address_line_2   : PropTypes.string,
    contact_address_town_city: PropTypes.string,
    contact_address_post_code: PropTypes.string,
    billing_address_line_1   : PropTypes.string,
    billing_address_line_2   : PropTypes.string,
    billing_address_town_city: PropTypes.string,
    billing_address_post_code: PropTypes.string,
    order_id                 : PropTypes.string,
    uuid                     : PropTypes.string,
    cover_donation_fee: PropTypes.string,
  }),
  donationFeeRate        : PropTypes.number,
}
